import type { Candle } from "./api.ts";

export type VwapCurveToggle = "session" | "avwap" | "D" | "W" | "M";

export interface VwapCurvePrefs {
  session: boolean;
  avwap: boolean;
  D: boolean;
  W: boolean;
  M: boolean;
}

export interface VwapCurvePoint {
  t: number;
  value: number | null;
  numerator: number;
  denominator: number;
}

export interface VwapCurve {
  id: string;
  anchorTs: number;
  points: VwapCurvePoint[];
}

export function sameVwapCurvePoints(
  left: ReadonlyArray<{ t: number; value: number | null }>,
  right: ReadonlyArray<{ t: number; value: number | null }>,
): boolean {
  return left.length === right.length &&
    left.every((point, index) => point.t === right[index].t && point.value === right[index].value);
}

/** Resolve a chart time only when it falls inside an actually held candle, never across a gap. */
export function anchorBarAtTime(candles: readonly Candle[], at: number, barMs: number): Candle | null {
  if (!Number.isFinite(at) || !Number.isFinite(barMs) || barMs <= 0) return null;
  return candles.find((candle) => Number.isFinite(candle.t) && at >= candle.t && at < candle.t + barMs) ?? null;
}

export const DEFAULT_VWAP_CURVE_PREFS: VwapCurvePrefs = {
  session: false,
  avwap: false,
  D: false,
  W: false,
  M: false,
};

export const VWAP_CURVE_LIMIT = 6;
export const VWAP_CURVE_PREFS_KEY = "spx-hub.vwap-curves.v1";
export const CALENDAR_VWAP_CURVE_IDS = {
  session: "vwapCurve:session",
  D: "vwapCurve:D",
  W: "vwapCurve:W",
  M: "vwapCurve:M",
} as const;
export const PULLED_AVWAP_CURVE_PREFIX = "avwapCurve:";

export type VwapCurveToken = "--vwap" | "--magnet" | "--up" | "--flip";

/** Owner-ruled BTC-hub parity: D blue, W yellow, M green, pulled anchors purple. */
export function vwapCurveToken(id: string): VwapCurveToken {
  if (id === CALENDAR_VWAP_CURVE_IDS.session || id === CALENDAR_VWAP_CURVE_IDS.D) return "--vwap";
  if (id === CALENDAR_VWAP_CURVE_IDS.W) return "--magnet";
  if (id === CALENDAR_VWAP_CURVE_IDS.M) return "--up";
  if (id.startsWith(PULLED_AVWAP_CURVE_PREFIX)) return "--flip";
  throw new TypeError(`unknown VWAP curve id ${id}`);
}

export type VwapLevelToggle = Exclude<VwapCurveToggle, "avwap">;

/** The 26-S2 horizontal levels share the curve buttons; there is no independent level switch. */
export function vwapLevelToggle(level: { kind: string }): VwapLevelToggle {
  if (level.kind === "session") return "session";
  if (level.kind === "prior-day" || level.kind === "naked-day") return "D";
  if (level.kind === "naked-week") return "W";
  if (level.kind === "naked-month") return "M";
  throw new TypeError(`unknown VWAP level kind ${level.kind}`);
}

export function vwapLevelVisible(level: { kind: string }, prefs: VwapCurvePrefs): boolean {
  return prefs[vwapLevelToggle(level)];
}

type StoredPrefs = Record<string, unknown>;
interface StoredPrefsRead {
  stored: StoredPrefs;
  error: string | null;
}

function browserStorage(): Storage | null {
  try {
    return typeof localStorage === "undefined" ? null : localStorage;
  } catch {
    return null;
  }
}

function readStored(storage: Storage | null): StoredPrefsRead {
  if (!storage) {
    const error = "preference storage unavailable; VWAP curve toggles will not persist";
    console.error(`[vwap-curves] ${error}`);
    return { stored: {}, error };
  }
  try {
    const parsed: unknown = JSON.parse(storage.getItem(VWAP_CURVE_PREFS_KEY) ?? "{}");
    if (typeof parsed !== "object" || parsed === null || Array.isArray(parsed)) {
      const error = "stored VWAP curve preferences have an invalid shape";
      console.error(`[vwap-curves] ${error}`);
      return { stored: {}, error };
    }
    return { stored: parsed as StoredPrefs, error: null };
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error);
    console.error(`[vwap-curves] preference read failed — ${message}`);
    return { stored: {}, error: `preference read failed — ${message}` };
  }
}

export function storedVwapCurvePrefs(
  panelId: string,
  storage = browserStorage(),
  legacyPrefixes: readonly string[] = [],
): { prefs: VwapCurvePrefs; error: string | null } {
  const read = readStored(storage);
  const legacyKeys = Object.keys(read.stored).filter((key) =>
    key !== panelId && legacyPrefixes.some((prefix) => key.startsWith(prefix))
  );
  const legacyKey = legacyKeys.find((key) => legacyPrefixes.includes(key)) ?? legacyKeys.sort()[0];
  const hasStableRecord = Object.prototype.hasOwnProperty.call(read.stored, panelId);
  const candidate = hasStableRecord
    ? read.stored[panelId]
    : legacyKey === undefined ? undefined : read.stored[legacyKey];
  const invalidPanel = candidate !== undefined &&
    (typeof candidate !== "object" || candidate === null || Array.isArray(candidate));
  const raw = invalidPanel ? {} : (candidate ?? {}) as Partial<VwapCurvePrefs>;
  const panelError = invalidPanel ? `stored VWAP curve preferences have an invalid panel record for ${panelId}` : null;
  if (panelError !== null) console.error(`[vwap-curves] ${panelError}`);
  const prefs = Object.fromEntries(
    (Object.keys(DEFAULT_VWAP_CURVE_PREFS) as VwapCurveToggle[])
      .map((key) => [key, typeof raw[key] === "boolean" ? raw[key] : DEFAULT_VWAP_CURVE_PREFS[key]]),
  ) as unknown as VwapCurvePrefs;
  let migrationError: string | null = null;
  if (storage !== null && !hasStableRecord && legacyKey !== undefined && !invalidPanel) {
    try {
      const migrated = { ...read.stored, [panelId]: candidate };
      for (const key of legacyKeys) delete migrated[key];
      storage.setItem(VWAP_CURVE_PREFS_KEY, JSON.stringify(migrated));
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      migrationError = `preference migration failed — ${message}`;
      console.error(`[vwap-curves] ${migrationError}`);
    }
  }
  return { prefs, error: read.error ?? panelError ?? migrationError };
}

export function rememberVwapCurvePrefs(
  panelId: string,
  prefs: VwapCurvePrefs,
  storage = browserStorage(),
): string | null {
  try {
    if (!storage) {
      const message = "preference storage unavailable; VWAP curve toggles will not persist";
      console.error(`[vwap-curves] ${message}`);
      return message;
    }
    const previous = readStored(storage);
    storage.setItem(VWAP_CURVE_PREFS_KEY, JSON.stringify({
      ...previous.stored,
      [panelId]: prefs,
    }));
    return null;
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error);
    console.error(`[vwap-curves] preference write failed — ${message}`);
    return `preference write failed — ${message}`;
  }
}

/** Running HLC3×volume VWAP. Bars without a finite positive volume carry no weight and no point. */
export function anchoredVwapCurve(candles: readonly Candle[], anchorTs: number, id: string): VwapCurve {
  if (!Number.isFinite(anchorTs)) throw new TypeError("VWAP anchor must be finite");
  let numerator = 0;
  let denominator = 0;
  const points: VwapCurvePoint[] = [];
  const ordered = candles.slice().sort((a, b) => a.t - b.t);
  for (const candle of ordered) {
    if (candle.t < anchorTs || !Number.isFinite(candle.t)) continue;
    if (candle.v === null || !Number.isFinite(candle.v) || candle.v <= 0) {
      points.push({ t: candle.t, value: null, numerator, denominator });
      continue;
    }
    const hlc3 = (candle.h + candle.l + candle.c) / 3;
    if (!Number.isFinite(hlc3)) {
      points.push({ t: candle.t, value: null, numerator, denominator });
      continue;
    }
    numerator += hlc3 * candle.v;
    denominator += candle.v;
    points.push({ t: candle.t, value: numerator / denominator, numerator, denominator });
  }
  return { id, anchorTs, points };
}

/** Keep native-1m math while publishing only timestamps already owned by the selected chart rung. */
export function sampleVwapCurve(
  curve: VwapCurve,
  windows: readonly { t: number; to: number }[],
): VwapCurve {
  const usable = curve.points.filter((point) => point.value !== null).sort((a, b) => a.t - b.t);
  const sampled: VwapCurvePoint[] = [];
  let index = 0;
  let latest: VwapCurvePoint | null = null;
  for (const window of windows.slice().sort((a, b) => a.t - b.t)) {
    while (index < usable.length && usable[index].t < window.to) {
      latest = usable[index];
      index += 1;
    }
    if (window.t >= curve.anchorTs && latest !== null && latest.t >= window.t) {
      sampled.push({ ...latest, t: window.t });
    }
  }
  return { ...curve, points: sampled };
}

interface ChicagoParts {
  year: number;
  month: number;
  day: number;
  hour: number;
}

function chicagoParts(at: number): ChicagoParts {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: "America/Chicago",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    hour12: false,
  }).formatToParts(new Date(at));
  const read = (type: string) => Number(parts.find((part) => part.type === type)?.value ?? NaN);
  return { year: read("year"), month: read("month"), day: read("day"), hour: read("hour") % 24 };
}

function chicagoEpoch(parts: ChicagoParts, dayShift: number, hour: number): number {
  const noonGuess = Date.UTC(parts.year, parts.month - 1, parts.day + dayShift, 12);
  const atNoon = chicagoParts(noonGuess);
  const localAsUtc = Date.UTC(atNoon.year, atNoon.month - 1, atNoon.day, atNoon.hour);
  const offset = localAsUtc - noonGuess;
  return Date.UTC(parts.year, parts.month - 1, parts.day + dayShift, hour) - offset;
}

function shiftedDate(parts: ChicagoParts, dayShift: number): ChicagoParts {
  const date = new Date(Date.UTC(parts.year, parts.month - 1, parts.day + dayShift, 12));
  return { year: date.getUTCFullYear(), month: date.getUTCMonth() + 1, day: date.getUTCDate(), hour: 12 };
}

function cmeSessionDateParts(at: number): ChicagoParts {
  const local = chicagoParts(at);
  return shiftedDate(local, local.hour >= 17 ? 0 : -1);
}

/** Most recent 17:00 America/Chicago ES session open, DST-aware. */
export function cmeSessionAnchor(at: number): number {
  if (!Number.isFinite(at)) throw new TypeError("session VWAP timestamp must be finite");
  const parts = chicagoParts(at);
  return chicagoEpoch(parts, parts.hour >= 17 ? 0 : -1, 17);
}

/** CME D/W/M boundaries all open at 17:00 Chicago; Sunday opens a new trading week. */
export function calendarVwapAnchor(scope: "D" | "W" | "M", at: number): number {
  if (!Number.isFinite(at)) throw new TypeError("calendar VWAP timestamp must be finite");
  const sessionDate = cmeSessionDateParts(at);
  let anchorDate = sessionDate;
  if (scope === "W") {
    const weekday = new Date(Date.UTC(sessionDate.year, sessionDate.month - 1, sessionDate.day, 12)).getUTCDay();
    anchorDate = shiftedDate(sessionDate, -weekday);
  } else if (scope === "M") {
    anchorDate = { ...sessionDate, day: 1 };
  }
  return chicagoEpoch(anchorDate, 0, 17);
}

export function buildVwapCurves(
  candles: readonly Candle[],
  options: {
    sessionAnchorTs?: number;
    /** Latest live chart timestamp; calendar identity must not lag retained native coverage. */
    latestTs?: number;
    /** Earliest anchor proven by bounded native reads plus a retained pre-anchor witness. */
    coverageFromTs?: number;
    renderWindows?: readonly { t: number; to: number }[];
    pulledAnchors: readonly number[];
    visible: VwapCurvePrefs;
  },
): { all: VwapCurve[]; drawn: VwapCurve[]; more: number; incomplete: string[] } {
  const times = candles.map((candle) => candle.t).filter(Number.isFinite);
  const sourceLatest = times.length ? Math.max(...times) : undefined;
  if (sourceLatest === undefined) return { all: [], drawn: [], more: 0, incomplete: [] };
  const latest = Number.isFinite(options.latestTs) ? options.latestTs! : sourceLatest;
  const all: VwapCurve[] = [];
  const incomplete: string[] = [];
  const oldest = Math.min(
    ...times,
    Number.isFinite(options.coverageFromTs) ? options.coverageFromTs! : Number.POSITIVE_INFINITY,
  );
  const addCurve = (anchor: number, id: string) => {
    const curve = anchoredVwapCurve(candles, anchor, id);
    if (!curve.points.some((point) => point.value !== null)) incomplete.push(id);
    else {
      const rendered = options.renderWindows === undefined ? curve : sampleVwapCurve(curve, options.renderWindows);
      if (rendered.points.length === 0) incomplete.push(id);
      else all.push(rendered);
    }
  };
  const addCalendarCurve = (anchor: number, id: string) => {
    if (oldest > anchor) {
      incomplete.push(id);
      return;
    }
    addCurve(anchor, id);
  };
  if (options.visible.session) {
    const anchor = options.sessionAnchorTs ?? cmeSessionAnchor(latest);
    addCalendarCurve(anchor, CALENDAR_VWAP_CURVE_IDS.session);
  }
  for (const scope of ["D", "W", "M"] as const) {
    if (options.visible[scope]) {
      addCalendarCurve(calendarVwapAnchor(scope, latest), CALENDAR_VWAP_CURVE_IDS[scope]);
    }
  }
  if (options.visible.avwap) {
    const unique = [...new Set(options.pulledAnchors.filter(Number.isFinite))].sort((a, b) => b - a);
    for (const anchor of unique) {
      const id = `${PULLED_AVWAP_CURVE_PREFIX}${anchor}`;
      if (oldest > anchor) incomplete.push(id);
      else addCurve(anchor, id);
    }
  }
  return {
    all,
    drawn: all.slice(0, VWAP_CURVE_LIMIT),
    more: Math.max(0, all.length - VWAP_CURVE_LIMIT),
    incomplete,
  };
}
